/**
 * OutreachReviewQueue
 *
 * Manager / Coordinator review surface for the Phase 5 approval gate.
 * Lists outreach entries in `status='submitted'`, lets the reviewer
 * inspect a quick summary of each, and approve or reject with an
 * optional note. After a decision, the row drops out of the queue.
 *
 * Approved entries are what the VYC quarterly report (Phase 3) will
 * count once Raylene's template arrives. Rejected entries flip back
 * to 'submitted' once the Promotora updates and re-submits via the
 * existing PUT path.
 *
 * Server-side authorization: the underlying POST endpoints are gated
 * to PRIVILEGED_EDIT_ROLES; if a frontline-staff user lands here
 * directly the action buttons return 403 with a clear message.
 */

import React, { useEffect, useState } from 'react';
import { CheckCircle2, XCircle, AlertTriangle, Clock, RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import {
  approveOutreachActivity,
  listOutreachActivities,
  OutreachActivity,
  rejectOutreachActivity,
} from '@/services/api/outreachActivities';

export const OutreachReviewQueue: React.FC = () => {
  const [rows, setRows] = useState<OutreachActivity[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [actingId, setActingId] = useState<number | null>(null);

  const load = () => {
    setRows(null);
    setError(null);
    listOutreachActivities({ status: 'submitted', limit: 200 })
      .then(setRows)
      .catch((e: any) => setError(e?.message || 'Failed to load review queue.'));
  };

  useEffect(load, []);

  const handleDecision = async (
    id: number,
    decision: 'approve' | 'reject',
    notes: string,
  ) => {
    setActingId(id);
    const fn = decision === 'approve' ? approveOutreachActivity : rejectOutreachActivity;
    const result = await fn(id, { notes: notes.trim() || undefined });
    setActingId(null);
    if (!result.ok) {
      setError(`${decision === 'approve' ? 'Approve' : 'Reject'} failed: ${result.message}`);
      return;
    }
    // Drop the decided row from the queue
    setRows((prev) => (prev ? prev.filter((r) => r.id !== id) : prev));
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex justify-between items-start gap-2">
          <div>
            <CardTitle className="flex items-center gap-2">
              <Clock className="w-5 h-5 text-amber-600" />
              Outreach Review Queue
              {rows && rows.length > 0 && (
                <Badge variant="outline" className="ml-1 text-xs">
                  {rows.length} pending
                </Badge>
              )}
            </CardTitle>
            <p className="text-sm text-gray-500 mt-1">
              Outreach entries waiting for supervisor review. Only approved entries count toward
              the VYC quarterly report.
            </p>
          </div>
          <Button variant="ghost" size="sm" onClick={load}>
            <RefreshCw className="w-4 h-4 mr-1" />
            Refresh
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        {error && (
          <div className="flex gap-2 items-start text-sm bg-red-50 border border-red-200 text-red-900 rounded-md p-3">
            <AlertTriangle className="w-4 h-4 mt-0.5 text-red-600" />
            <span>{error}</span>
          </div>
        )}
        {rows === null && !error ? (
          <div className="text-sm text-gray-500 py-6 text-center">Loading…</div>
        ) : rows && rows.length === 0 ? (
          <div className="text-sm text-gray-500 py-6 text-center">
            <CheckCircle2 className="w-5 h-5 mx-auto text-green-500 mb-2" />
            No entries awaiting review. The team is up to date.
          </div>
        ) : (
          rows!.map((row) => (
            <ReviewRow
              key={row.id}
              row={row}
              busy={actingId === row.id}
              onDecision={(decision, notes) => handleDecision(row.id, decision, notes)}
            />
          ))
        )}
      </CardContent>
    </Card>
  );
};

interface ReviewRowProps {
  row: OutreachActivity;
  busy: boolean;
  onDecision: (decision: 'approve' | 'reject', notes: string) => void;
}

const ReviewRow: React.FC<ReviewRowProps> = ({ row, busy, onDecision }) => {
  const [notes, setNotes] = useState('');
  const fd = row.field_data || {};

  return (
    <div className="border border-gray-200 dark:border-gray-700 rounded-md p-3 space-y-2">
      <div className="flex items-center gap-2 flex-wrap">
        <span className="font-medium">{fd.outreach_activity_type || 'Outreach activity'}</span>
        {fd.outreach_engagement_type && (
          <Badge variant="outline" className="text-xs">
            {fd.outreach_engagement_type}
          </Badge>
        )}
        <Badge variant="outline" className="text-[10px] uppercase">
          #{row.id}
        </Badge>
      </div>

      <div className="text-xs text-gray-600 dark:text-gray-400 grid grid-cols-2 md:grid-cols-4 gap-x-3 gap-y-1">
        {fd.outreach_activity_date && (
          <div>
            <span className="text-gray-400">Date:</span> {String(fd.outreach_activity_date).slice(0, 10)}
          </div>
        )}
        {fd.outreach_num_immigrants_refugees_reached !== undefined && (
          <div>
            <span className="text-gray-400">Reached:</span> {fd.outreach_num_immigrants_refugees_reached}
          </div>
        )}
        {fd.outreach_location_of_outreach && (
          <div className="truncate">
            <span className="text-gray-400">Location:</span> {fd.outreach_location_of_outreach}
          </div>
        )}
        {Array.isArray(fd.outreach_languages_used) && fd.outreach_languages_used.length > 0 && (
          <div className="truncate">
            <span className="text-gray-400">Languages:</span> {fd.outreach_languages_used.join(', ')}
          </div>
        )}
      </div>

      {row.notes && (
        <div className="text-xs text-gray-600 dark:text-gray-400 bg-gray-50 dark:bg-gray-800 rounded p-2 whitespace-pre-wrap">
          {row.notes}
        </div>
      )}

      <div className="space-y-2 pt-1">
        <Textarea
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          rows={2}
          placeholder="Review note (optional — visible in audit trail)"
          disabled={busy}
        />
        <div className="flex gap-2 justify-end">
          <Button
            variant="outline"
            size="sm"
            disabled={busy}
            onClick={() => onDecision('reject', notes)}
          >
            <XCircle className="w-4 h-4 mr-1 text-red-600" />
            Reject
          </Button>
          <Button size="sm" disabled={busy} onClick={() => onDecision('approve', notes)}>
            <CheckCircle2 className="w-4 h-4 mr-1" />
            Approve
          </Button>
        </div>
      </div>
    </div>
  );
};

export default OutreachReviewQueue;
